import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { colors } from "../../../theme/colors";

interface PerformanceCardProps {
  title: string;
  icon: React.ReactNode;
  borderColor: string;
  loading: boolean;
  data: {
    office?: string;
    department?: string;
    institution?: string;
    satisfactionScore?: number;
    fastServiceScore?: number;
    totalResponses: number;
  } | null;
  metricLabel: string; // e.g., "positive ratings" or "under 30 min"
}

// Consolidated card styles - edit these to change all cards at once
const CARD_STYLES = {
  // Card container
  backgroundColor: "#002c64", // Change background color here
  borderRadius: "10px", // Change border radius here
  padding: "1.5rem",
  border: "1px solid #e0e0e0",
  transition: "transform 0.2s, box-shadow 0.2s",

  // Header
  headerGap: 1.5,
  titleColor: "#e0e0e0",
  titleFontSize: "1rem",
  titleFontWeight: 600,

  // Office/Department name
  nameColor: "#e0e0e0", // Change office name color here
  nameFontSize: "1.5rem",
  nameFontWeight: 700,

  // Rating/Score text
  ratingColor: "#e0e0e0", // Change rating text color here
  ratingFontSize: "0.875rem",

  // Response count text
  responseColor: "#999", // Change response text color here
  responseFontSize: "0.75rem",

  // Loading spinner
  spinnerBorderColor: "#e0e0e0",
  spinnerSize: 20,
  spinnerBorderWidth: "3px",
};

const PerformanceCard: React.FC<PerformanceCardProps> = ({
  title,
  icon,
  borderColor,
  loading,
  data,
  metricLabel,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        // backgroundColor: CARD_STYLES.backgroundColor,
        backgroundColor: colors.bgPrimary,
        // borderRadius: CARD_STYLES.borderRadius,
        borderRadius: "20px",
        padding: CARD_STYLES.padding,
        border: CARD_STYLES.border,
        borderLeft: `4px solid ${borderColor}`, // Individual border color
        // borderLeft: `4px solid #FF9933`, // Individual border color
        transition: CARD_STYLES.transition,
        "&:hover": {
          transform: "translateY(-2px)",
          // boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        },
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: CARD_STYLES.headerGap,
          mb: 1,
        }}
      >
        {/* {icon} */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: CARD_STYLES.titleFontWeight,
            fontSize: CARD_STYLES.titleFontSize,
            color: CARD_STYLES.titleColor,
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Content */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            py: 2,
          }}
        >
          <Box
            sx={{
              width: CARD_STYLES.spinnerSize,
              height: CARD_STYLES.spinnerSize,
              border: `${CARD_STYLES.spinnerBorderWidth} solid ${CARD_STYLES.spinnerBorderColor}`,
              borderTop: `${CARD_STYLES.spinnerBorderWidth} solid ${borderColor}`,
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          <Typography variant="body2" sx={{ color: CARD_STYLES.ratingColor }}>
            Loading...
          </Typography>
        </Box>
      ) : data ? (
        <>
          <Typography
            variant="h6"
            sx={{
              fontWeight: CARD_STYLES.nameFontWeight,
              fontSize: CARD_STYLES.nameFontSize,
              color: CARD_STYLES.nameColor,
              mb: 0.5,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {data.office || data.department || data.institution || "N/A"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: CARD_STYLES.ratingColor,
              fontSize: CARD_STYLES.ratingFontSize,
            }}
          >
            {data.satisfactionScore ?? data.fastServiceScore}% {metricLabel}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: CARD_STYLES.responseColor,
              fontSize: CARD_STYLES.responseFontSize,
            }}
          >
            {data.totalResponses.toLocaleString()} responses
          </Typography>
        </>
      ) : (
        <Typography variant="body2" sx={{ color: CARD_STYLES.ratingColor }}>
          No data available
        </Typography>
      )}
    </Paper>
  );
};

export default PerformanceCard;
